/**
 * Map Canvas Component
 * SVG overlay for tracing polygons on the site plan image
 */
import { useRef, useEffect, useState, useCallback } from 'react';
import { cn } from '@/lib/utils';

interface MapCanvasProps {
  imageUrl: string;
  viewBox: string;
  currentPolygon: number[][];
  existingPolygons: Map<string, { points: number[][]; color?: string }>;
  selectedUnit: string | null;
  onPointAdd: (point: [number, number], insertIndex?: number) => void;
  onPointDelete?: (index: number) => void;
  onPointMove?: (index: number, point: [number, number]) => void;
  onPolygonMove?: (deltaX: number, deltaY: number) => void;
  onPolygonMoveStart?: () => void;
  zoom?: number;
  pan?: { x: number; y: number };
  onPanChange?: (pan: { x: number; y: number }) => void;
  hiddenPolygons?: Set<string>;
  currentPolygonKey?: string | null; // Key for the current polygon (e.g., "A-1" or "A-01-GF")
  imageWidth?: number; // Actual image width in pixels (for raster images)
  imageHeight?: number; // Actual image height in pixels (for raster images)
  className?: string;
}

export function MapCanvas({
  imageUrl,
  viewBox,
  currentPolygon,
  existingPolygons,
  selectedUnit,
  onPointAdd,
  onPointDelete,
  onPointMove,
  onPolygonMove,
  onPolygonMoveStart,
  zoom = 1,
  pan = { x: 0, y: 0 },
  onPanChange,
  hiddenPolygons = new Set(),
  currentPolygonKey = null,
  imageWidth,
  imageHeight,
  className,
}: MapCanvasProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<{ x: number; y: number; panX: number; panY: number } | null>(null);
  const [hoveredPointIndex, setHoveredPointIndex] = useState<number | null>(null);
  const [draggingPointIndex, setDraggingPointIndex] = useState<number | null>(null);
  const [draggingPolygon, setDraggingPolygon] = useState(false);
  const [dragStartPoint, setDragStartPoint] = useState<[number, number] | null>(null);
  const [dragStartSVGPoint, setDragStartSVGPoint] = useState<[number, number] | null>(null);
  const [lastClickPoint, setLastClickPoint] = useState<[number, number] | null>(null);

  // Parse viewBox and calculate zoomed viewBox (needed for coordinate conversion)
  const [vbX, vbY, vbWidth, vbHeight] = viewBox.split(' ').map(Number);
  const centerX = vbX + vbWidth / 2;
  const centerY = vbY + vbHeight / 2;
  const zoomedWidth = vbWidth / zoom;
  const zoomedHeight = vbHeight / zoom;
  const zoomedX = centerX - zoomedWidth / 2 + pan.x;
  const zoomedY = centerY - zoomedHeight / 2 + pan.y;

  // Convert screen coordinates to SVG coordinates
  // Uses getScreenCTM() which properly accounts for preserveAspectRatio and viewBox
  // Falls back to manual calculation if CTM is not available
  const screenToSVG = useCallback(
    (e: React.MouseEvent<SVGSVGElement> | MouseEvent, clientX?: number, clientY?: number): [number, number] | null => {
      if (!svgRef.current) return null;

      const svg = svgRef.current;
      const rect = svg.getBoundingClientRect();
      
      // Support both React events and native MouseEvent
      const x = clientX !== undefined ? clientX : (e as React.MouseEvent<SVGSVGElement>).clientX;
      const y = clientY !== undefined ? clientY : (e as React.MouseEvent<SVGSVGElement>).clientY;
      
      // Get mouse position relative to SVG element (in pixels)
      const screenX = x - rect.left;
      const screenY = y - rect.top;
      
      // Try using getScreenCTM() first - this is the most reliable method
      // It automatically accounts for viewBox, preserveAspectRatio, zoom, pan, etc.
      const ctm = svg.getScreenCTM();
      if (ctm) {
        // Create a point in SCREEN coordinates (clientX, clientY)
        // getScreenCTM() transforms from SVG user space to screen space
        // So ctm.inverse() needs SCREEN coordinates (not relative to SVG element)
        const point = svg.createSVGPoint();
        point.x = x;  // Use original clientX, NOT screenX (which is relative to SVG)
        point.y = y;  // Use original clientY, NOT screenY
        
        // Transform from screen coordinates to SVG user coordinates
        // This gives us coordinates in the SVG's current viewBox space (zoomedViewBox)
        const svgPointZoomed = point.matrixTransform(ctm.inverse());
        
        // Get the actual viewBox from the SVG element
        const actualViewBox = svg.viewBox.baseVal;
        
        // Convert from zoomed viewBox coordinates to base viewBox coordinates
        // Formula: baseCoord = ((zoomedCoord - zoomedOrigin) / zoomedSize) * baseSize + baseOrigin
        const baseX = ((svgPointZoomed.x - actualViewBox.x) / actualViewBox.width) * vbWidth + vbX;
        const baseY = ((svgPointZoomed.y - actualViewBox.y) / actualViewBox.height) * vbHeight + vbY;
        
        // Debug logging
        console.log('Click conversion (CTM):', {
          clientCoords: { x, y },
          screenRelative: { x: screenX, y: screenY },
          svgZoomed: { x: svgPointZoomed.x, y: svgPointZoomed.y },
          svgBase: { x: baseX, y: baseY },
          rect: { left: rect.left, top: rect.top, width: rect.width, height: rect.height },
          actualViewBox: { x: actualViewBox.x, y: actualViewBox.y, width: actualViewBox.width, height: actualViewBox.height },
          zoomedViewBox: { x: zoomedX, y: zoomedY, width: zoomedWidth, height: zoomedHeight },
          baseViewBox: { x: vbX, y: vbY, width: vbWidth, height: vbHeight },
          zoom,
          pan,
        });
        
        return [baseX, baseY];
      }
      
      // Fallback: Manual calculation if CTM is not available
      // This should rarely be needed, but provides a backup
      console.warn('getScreenCTM() returned null, using manual calculation');
      const viewBox = svg.viewBox.baseVal;
      const zoomedVbX = viewBox.x;
      const zoomedVbY = viewBox.y;
      const zoomedVbWidth = viewBox.width;
      const zoomedVbHeight = viewBox.height;
      
      // Calculate scale factors based on zoomed viewBox and screen dimensions
      const scaleX = zoomedVbWidth / rect.width;
      const scaleY = zoomedVbHeight / rect.height;
      
      // Convert screen coordinates to zoomed SVG coordinates
      const svgXZoomed = zoomedVbX + screenX * scaleX;
      const svgYZoomed = zoomedVbY + screenY * scaleY;
      
      // Convert from zoomed viewBox coordinates to base coordinate system (0 0 1728 864)
      const baseX = ((svgXZoomed - zoomedX) / zoomedWidth) * vbWidth + vbX;
      const baseY = ((svgYZoomed - zoomedY) / zoomedHeight) * vbHeight + vbY;
      
      console.log('Click conversion (manual):', {
        screen: { x: screenX, y: screenY },
        svgZoomed: { x: svgXZoomed, y: svgYZoomed },
        svgBase: { x: baseX, y: baseY },
        rect: { width: rect.width, height: rect.height },
        zoomedViewBox: { x: zoomedVbX, y: zoomedVbY, width: zoomedVbWidth, height: zoomedVbHeight },
        baseViewBox: { x: vbX, y: vbY, width: vbWidth, height: vbHeight },
        imageDimensions: imageWidth && imageHeight ? { width: imageWidth, height: imageHeight } : null,
        scale: { x: scaleX, y: scaleY },
        zoom,
        pan,
      });
      
      return [baseX, baseY];
    },
    [zoom, pan, viewBox, imageWidth, imageHeight] // Include zoom, pan, viewBox, and image dimensions as dependencies
  );

  // Track if spacebar is held for pan mode
  const [spacePressed, setSpacePressed] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space' && zoom > 1) {
        e.preventDefault();
        setSpacePressed(true);
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        setSpacePressed(false);
        setIsDragging(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [zoom]);

  const handleMouseDown = (e: React.MouseEvent<SVGSVGElement>) => {
    const target = e.target as SVGElement;
    const isPointClick = target.tagName === 'circle' || target.closest('circle') !== null;
    const isPolygonClick = target.tagName === 'polygon' || target.tagName === 'polyline';
    const isImageClick = target.tagName === 'image';
    const isSvgClick = target.tagName === 'svg';
    
    console.log('MouseDown:', {
      target: target.tagName,
      isPointClick,
      isPolygonClick,
      isImageClick,
      isSvgClick,
      button: e.button,
      zoom,
    });
    
    // Check if clicking on a point in current polygon
    if (isPointClick && currentPolygon.length > 0) {
      const circle = target.tagName === 'circle' ? target : target.closest('circle');
      if (circle) {
        const pointIndex = parseInt(circle.getAttribute('data-point-index') || '-1');
        if (pointIndex >= 0 && onPointMove) {
          e.preventDefault();
          e.stopPropagation();
          setDraggingPointIndex(pointIndex);
          const svgPoint = screenToSVG(e);
          if (svgPoint) {
            setDragStartSVGPoint(svgPoint);
            // Store the original point position
            setDragStartPoint([currentPolygon[pointIndex][0], currentPolygon[pointIndex][1]]);
          }
          return;
        }
      }
    }
    
    // Check if clicking on current polygon (to move entire polygon)
    if (isPolygonClick && currentPolygon.length >= 3 && onPolygonMove) {
      const polygon = target.tagName === 'polygon' ? target : target.closest('polygon');
      if (polygon && polygon.getAttribute('data-current-polygon') === 'true') {
        e.preventDefault();
        e.stopPropagation();
        setDraggingPolygon(true);
        const svgPoint = screenToSVG(e);
        if (svgPoint) {
          setDragStartSVGPoint(svgPoint);
          // Notify parent to store initial polygon position
          onPolygonMoveStart?.();
        }
        return;
      }
    }
    
    // Also check if clicking inside the polygon area (for easier selection)
    // Only check if we haven't already handled a point or polygon click
    if (currentPolygon.length >= 3 && onPolygonMove && e.button === 0 && !isPointClick && !isPolygonClick) {
      const svgPoint = screenToSVG(e);
      if (svgPoint) {
        const inside = isPointInPolygon(svgPoint, currentPolygon);
        console.log('Checking polygon click:', { svgPoint, inside, target: (e.target as SVGElement).tagName });
        // Only start dragging if clicking on SVG background (not on other elements) and inside polygon
        if (inside && (e.target as SVGElement).tagName === 'svg') {
          e.preventDefault();
          e.stopPropagation();
          setDraggingPolygon(true);
          setDragStartSVGPoint(svgPoint);
          onPolygonMoveStart?.();
          console.log('Starting polygon drag');
          return;
        }
      }
    }
    
    // When zoomed in, allow pan with:
    // - Middle mouse button (button === 1)
    // - Right mouse button (button === 2)
    // - Space + left click
    // - Ctrl/Cmd + left click
    const canPanImmediately = zoom > 1 && (
      e.button === 1 || // Middle mouse
      e.button === 2 || // Right mouse
      (e.button === 0 && (spacePressed || e.ctrlKey || e.metaKey)) // Modifier keys
    );

    if (canPanImmediately && !isPointClick && !isPolygonClick) {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
      setDragStart({
        x: e.clientX,
        y: e.clientY,
        panX: pan.x,
        panY: pan.y,
      });
    } else {
      // Track mouse position for potential drag detection or point add
      // Don't start dragging immediately - wait for mouse movement in handleMouseMove
      setDragStart({
        x: e.clientX,
        y: e.clientY,
        panX: pan.x,
        panY: pan.y,
      });
    }
  };

  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    // Handle dragging a point
    if (draggingPointIndex !== null && dragStartSVGPoint && dragStartPoint && onPointMove) {
      e.preventDefault();
      const currentSVGPoint = screenToSVG(e);
      if (currentSVGPoint) {
        // Calculate delta from initial mouse position to current mouse position
        const deltaX = currentSVGPoint[0] - dragStartSVGPoint[0];
        const deltaY = currentSVGPoint[1] - dragStartSVGPoint[1];
        
        // Update the point position: original point position + delta
        const newPoint: [number, number] = [
          dragStartPoint[0] + deltaX,
          dragStartPoint[1] + deltaY,
        ];
        onPointMove(draggingPointIndex, newPoint);
      }
      return;
    }
    
    // Handle dragging entire polygon
    if (draggingPolygon && dragStartSVGPoint && onPolygonMove) {
      e.preventDefault();
      e.stopPropagation();
      const currentSVGPoint = screenToSVG(e);
      if (currentSVGPoint) {
        // Calculate total delta from initial click position
        // Don't update dragStartSVGPoint - parent handles initial position via polygonStartRef
        const deltaX = currentSVGPoint[0] - dragStartSVGPoint[0];
        const deltaY = currentSVGPoint[1] - dragStartSVGPoint[1];
        console.log('Moving polygon:', { deltaX, deltaY, currentSVGPoint, dragStartSVGPoint });
        onPolygonMove(deltaX, deltaY);
      }
      return;
    }
    
    // Handle panning (canvas movement)
    if (isDragging && onPanChange && svgRef.current && dragStart) {
      e.preventDefault();
      const deltaX = e.clientX - dragStart.x;
      const deltaY = e.clientY - dragStart.y;
      
      // Convert screen pixels to SVG coordinates
      const rect = svgRef.current.getBoundingClientRect();
      const scaleX = vbWidth / rect.width;
      const scaleY = vbHeight / rect.height;
      
      // Pan in opposite direction of mouse movement
      const newPanX = dragStart.panX - deltaX * scaleX;
      const newPanY = dragStart.panY - deltaY * scaleY;
      
      onPanChange({ x: newPanX, y: newPanY });
    } else if (!isDragging && !draggingPointIndex && !draggingPolygon && e.buttons === 1 && zoom > 1 && dragStart) {
      // Check if user started dragging (moved mouse significantly)
      const moveDistance = Math.sqrt(
        Math.pow(e.clientX - dragStart.x, 2) + Math.pow(e.clientY - dragStart.y, 2)
      );
      
      // If moved more than 5 pixels, treat as drag (pan)
      // Only if clicking on SVG background, not on points or polygons
      const isPointClick = (e.target as SVGElement).tagName === 'circle' || 
                           (e.target as SVGElement).closest('circle') !== null;
      const isPolygonClick = (e.target as SVGElement).tagName === 'polygon' || 
                             (e.target as SVGElement).tagName === 'polyline';
      
      if (moveDistance > 5 && !isPointClick && !isPolygonClick && (e.target as SVGElement).tagName === 'svg') {
        setIsDragging(true);
        // Update dragStart with current pan values
        setDragStart({
          x: dragStart.x,
          y: dragStart.y,
          panX: pan.x,
          panY: pan.y,
        });
      }
    }
  };

  const handleMouseUp = (e: React.MouseEvent<SVGSVGElement>) => {
    const wasDraggingPoint = draggingPointIndex !== null;
    const wasDraggingPolygon = draggingPolygon;
    const wasDragging = isDragging;
    
    // Reset all drag states
    setIsDragging(false);
    setDraggingPointIndex(null);
    setDraggingPolygon(false);
    setDragStartPoint(null);
    setDragStartSVGPoint(null);
    
    if (wasDraggingPoint || wasDraggingPolygon || wasDragging) {
      e.preventDefault();
      e.stopPropagation();
      setDragStart(null);
      return;
    }
    
    // Only add point if:
    // 1. It was a left click (button === 0)
    // 2. Clicked on SVG background or image (not on existing elements like points or polygons)
    // 3. Not dragging (wasn't a pan gesture)
    // 4. Mouse didn't move much (was a click, not a drag)
    const target = e.target as SVGElement;
    const isPointClick = target.tagName === 'circle' || target.closest('circle') !== null;
    const isPolygonClick = target.tagName === 'polygon';
    const isPolylineClick = target.tagName === 'polyline';
    // Allow clicks on SVG background, image, or polyline (polyline handles its own insertion logic)
    const isClickableArea = target.tagName === 'svg' || target.tagName === 'image' || (isPolylineClick && !isPointClick && !isPolygonClick);
    
    console.log('MouseUp - checking for point add:', {
      button: e.button,
      target: target.tagName,
      isClickableArea,
      isPointClick,
      isPolygonClick,
      wasDragging,
      wasDraggingPoint,
      wasDraggingPolygon,
      hasDragStart: !!dragStart,
    });
    
    if (e.button === 0 && isClickableArea && dragStart && !wasDragging && !wasDraggingPoint && !wasDraggingPolygon) {
      const moveDistance = Math.sqrt(
        Math.pow(e.clientX - dragStart.x, 2) + Math.pow(e.clientY - dragStart.y, 2)
      );
      
      console.log('Move distance:', moveDistance);
      
      // If mouse moved less than 5 pixels, it's a click - add point
      if (moveDistance < 5) {
        console.log('Attempting to convert click to SVG coordinates...');
        const point = screenToSVG(e);
        console.log('Converted point:', point);
        if (point && !isNaN(point[0]) && !isNaN(point[1])) {
          // If clicking on polyline, let handleLineSegmentClick handle it (it already did via onClick)
          // Otherwise, add point normally (checking for segment insertion)
          if (!isPolylineClick) {
            const segmentInfo = currentPolygon.length > 1 ? findSegmentIndex(point) : null;
            console.log('MapCanvas: Calling onPointAdd with point:', point, 'segmentInfo:', segmentInfo);
            console.log('MapCanvas: onPointAdd function exists?', typeof onPointAdd === 'function');
            if (segmentInfo !== null) {
              // Use projected point if we found a segment
              setLastClickPoint(segmentInfo.projectedPoint);
              console.log('MapCanvas: Calling onPointAdd with projected point:', segmentInfo.projectedPoint, 'at index:', segmentInfo.index);
              onPointAdd(segmentInfo.projectedPoint, segmentInfo.index);
            } else {
              // Use raw click point if not on a segment
              setLastClickPoint(point);
              console.log('MapCanvas: Calling onPointAdd with raw point:', point);
              onPointAdd(point);
            }
            // Clear debug marker after a delay
            setTimeout(() => setLastClickPoint(null), 2000);
          }
          // If isPolylineClick, handleLineSegmentClick already handled it
        } else {
          console.error('Failed to convert click to SVG coordinates or got invalid point:', point);
        }
      } else {
        console.log('Move distance too large, treating as drag:', moveDistance);
      }
    } else {
      console.log('Not adding point - conditions not met');
    }
    
    setDragStart(null);
  };

  const handleSvgClick = (e: React.MouseEvent<SVGSVGElement>) => {
    // Click handling is done in handleMouseUp to distinguish clicks from drags
    // This handler is kept for compatibility but shouldn't add points
    // (points are added in handleMouseUp after confirming it's a click, not a drag)
  };

  // Prevent context menu when right-clicking for pan
  const handleContextMenu = (e: React.MouseEvent<SVGSVGElement>) => {
    if (zoom > 1) {
      e.preventDefault();
    }
  };

  const handlePointClick = (e: React.MouseEvent, index: number) => {
    e.stopPropagation();
    if (onPointDelete && e.detail === 2) {
      // Double click to delete
      onPointDelete(index);
    }
  };

  // Calculate which line segment was clicked and return the insertion index
  // Project a point onto a line segment and return the projected point
  const projectPointOntoLineSegment = (
    point: [number, number],
    lineStart: number[],
    lineEnd: number[]
  ): [number, number] => {
    const [px, py] = point;
    const [x1, y1] = lineStart;
    const [x2, y2] = lineEnd;
    
    const A = px - x1;
    const B = py - y1;
    const C = x2 - x1;
    const D = y2 - y1;
    
    const dot = A * C + B * D;
    const lenSq = C * C + D * D;
    let param = 0;
    
    if (lenSq !== 0) param = dot / lenSq;
    
    // Clamp param to [0, 1] to ensure point is on the segment
    param = Math.max(0, Math.min(1, param));
    
    // Calculate the projected point on the line segment
    const xx = x1 + param * C;
    const yy = y1 + param * D;
    
    return [xx, yy];
  };

  const findSegmentIndex = useCallback(
    (clickPoint: [number, number]): { index: number; projectedPoint: [number, number] } | null => {
      if (currentPolygon.length < 2) return null;

      let closestSegment: { index: number; distance: number; projectedPoint: [number, number] } | null = null;

      // Check each line segment to find which one was clicked
      for (let i = 0; i < currentPolygon.length; i++) {
        const p1 = currentPolygon[i];
        const p2 = currentPolygon[(i + 1) % currentPolygon.length];
        
        // Calculate distance from click point to line segment
        const dist = pointToLineDistance(clickPoint, p1, p2);
        
        // If click is within 20 pixels of the line segment, consider it
        if (dist < 20) {
          // Project the click point onto the line segment
          const projectedPoint = projectPointOntoLineSegment(clickPoint, p1, p2);
          
          // Check if projected point is between the two endpoints
          const segmentLength = Math.sqrt(
            Math.pow(p2[0] - p1[0], 2) + Math.pow(p2[1] - p1[1], 2)
          );
          const distToP1 = Math.sqrt(
            Math.pow(projectedPoint[0] - p1[0], 2) + Math.pow(projectedPoint[1] - p1[1], 2)
          );
          const distToP2 = Math.sqrt(
            Math.pow(projectedPoint[0] - p2[0], 2) + Math.pow(projectedPoint[1] - p2[1], 2)
          );
          
          // Only consider if projected point is between the endpoints
          if (distToP1 <= segmentLength * 1.01 && distToP2 <= segmentLength * 1.01) {
            // Keep track of the closest segment
            if (!closestSegment || dist < closestSegment.distance) {
              closestSegment = {
                index: i + 1, // Insert after point i
                distance: dist,
                projectedPoint,
              };
            }
          }
        }
      }
      
      return closestSegment ? { index: closestSegment.index, projectedPoint: closestSegment.projectedPoint } : null;
    },
    [currentPolygon]
  );

  // Calculate distance from a point to a line segment
  const pointToLineDistance = (
    point: [number, number],
    lineStart: number[],
    lineEnd: number[]
  ): number => {
    const [px, py] = point;
    const [x1, y1] = lineStart;
    const [x2, y2] = lineEnd;
    
    const A = px - x1;
    const B = py - y1;
    const C = x2 - x1;
    const D = y2 - y1;
    
    const dot = A * C + B * D;
    const lenSq = C * C + D * D;
    let param = -1;
    
    if (lenSq !== 0) param = dot / lenSq;
    
    let xx, yy;
    
    if (param < 0) {
      xx = x1;
      yy = y1;
    } else if (param > 1) {
      xx = x2;
      yy = y2;
    } else {
      xx = x1 + param * C;
      yy = y1 + param * D;
    }
    
    const dx = px - xx;
    const dy = py - yy;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleLineSegmentClick = (e: React.MouseEvent<SVGPolylineElement>) => {
    e.stopPropagation();
    // Don't preventDefault - let the normal click flow handle it
    const clickPoint = screenToSVG(e as unknown as React.MouseEvent<SVGSVGElement>);
    if (clickPoint && !isNaN(clickPoint[0]) && !isNaN(clickPoint[1])) {
      const segmentInfo = findSegmentIndex(clickPoint);
      console.log('Line segment clicked, segmentInfo:', segmentInfo);
      if (segmentInfo !== null) {
        // Use the projected point (exactly on the line) instead of the raw click point
        console.log('Inserting point at index:', segmentInfo.index, 'projected point:', segmentInfo.projectedPoint);
        setLastClickPoint(segmentInfo.projectedPoint);
        onPointAdd(segmentInfo.projectedPoint, segmentInfo.index);
        setTimeout(() => setLastClickPoint(null), 2000);
      } else {
        // If not on a segment, add to end
        console.log('Line clicked but not on segment, adding to end');
        setLastClickPoint(clickPoint);
        onPointAdd(clickPoint);
        setTimeout(() => setLastClickPoint(null), 2000);
      }
    }
  };

  const pointsString = currentPolygon.map((p) => `${p[0]},${p[1]}`).join(' ');

  // Use already calculated zoomed viewBox values for rendering
  const zoomedViewBox = `${zoomedX} ${zoomedY} ${zoomedWidth} ${zoomedHeight}`;
  
  // Log points string generation
  console.log('MapCanvas: Points string generation', {
    currentPolygonLength: currentPolygon.length,
    pointsString,
    pointsArray: currentPolygon,
    zoomedViewBox,
    baseViewBox: `${vbX} ${vbY} ${vbWidth} ${vbHeight}`,
  });

  // Helper function to check if a point is inside a polygon
  const isPointInPolygon = (point: [number, number], polygon: number[][]): boolean => {
    if (polygon.length < 3) return false;
    
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i][0], yi = polygon[i][1];
      const xj = polygon[j][0], yj = polygon[j][1];
      
      const intersect = ((yi > point[1]) !== (yj > point[1])) &&
        (point[0] < (xj - xi) * (point[1] - yi) / (yj - yi) + xi);
      if (intersect) inside = !inside;
    }
    return inside;
  };

  return (
    <div className={cn('relative w-full h-full bg-muted rounded-lg overflow-hidden', className)}>
      {/* Single SVG with image and overlay - ensures perfect coordinate alignment */}
      <svg
        ref={svgRef}
        viewBox={zoomedViewBox}
        className={`absolute inset-0 w-full h-full ${
          isDragging || spacePressed
            ? 'cursor-grabbing'
            : zoom > 1
            ? 'cursor-move'
            : 'cursor-crosshair'
        }`}
        preserveAspectRatio="xMidYMid meet"
        onClick={handleSvgClick}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onContextMenu={handleContextMenu}
        style={{ touchAction: 'none', userSelect: 'none' }}
        onLoad={() => {
          console.log('MapCanvas: SVG loaded', {
            viewBox: svgRef.current?.viewBox.baseVal ? {
              x: svgRef.current.viewBox.baseVal.x,
              y: svgRef.current.viewBox.baseVal.y,
              width: svgRef.current.viewBox.baseVal.width,
              height: svgRef.current.viewBox.baseVal.height,
            } : 'no SVG ref',
            zoomedViewBox,
            baseViewBox: `${vbX} ${vbY} ${vbWidth} ${vbHeight}`,
            currentPolygonLength: currentPolygon.length,
          });
        }}
      >
        {/* Background Image - Inside same SVG for perfect alignment */}
        {/* Remove preserveAspectRatio from image to ensure it fills the coordinate space exactly */}
        <image
          href={imageUrl}
          x={vbX}
          y={vbY}
          width={vbWidth}
          height={vbHeight}
          preserveAspectRatio="none"
          style={{ pointerEvents: 'none' }}
          onLoad={() => {
            console.log('MapCanvas: Image loaded in SVG', {
              imageUrl,
              imageX: vbX,
              imageY: vbY,
              imageWidth: vbWidth,
              imageHeight: vbHeight,
              viewBox: svgRef.current?.viewBox.baseVal ? {
                x: svgRef.current.viewBox.baseVal.x,
                y: svgRef.current.viewBox.baseVal.y,
                width: svgRef.current.viewBox.baseVal.width,
                height: svgRef.current.viewBox.baseVal.height,
              } : 'no SVG ref',
            });
          }}
          onError={(e) => {
            console.error('MapCanvas: Image failed to load in SVG', {
              imageUrl,
              error: e,
            });
          }}
        />
        {/* Existing Polygons - Filter out hidden ones */}
        {Array.from(existingPolygons.entries())
          .filter(([unitNumber]) => !hiddenPolygons.has(unitNumber))
          .map(([unitNumber, polygon]) => (
            <g key={unitNumber}>
              <polygon
                points={polygon.points.map((p) => `${p[0]},${p[1]}`).join(' ')}
                fill={polygon.color || 'rgba(59, 130, 246, 0.2)'}
                stroke={selectedUnit === unitNumber ? '#3b82f6' : '#94a3b8'}
                strokeWidth={selectedUnit === unitNumber ? 3 : 1}
                strokeOpacity={0.8}
                className="pointer-events-none"
              />
              {/* Unit Label - Extract unit number from key format "unitNumber-floor" */}
              <text
                x={
                  polygon.points.reduce((sum, p) => sum + p[0], 0) / polygon.points.length
                }
                y={
                  polygon.points.reduce((sum, p) => sum + p[1], 0) / polygon.points.length
                }
                textAnchor="middle"
                dominantBaseline="middle"
                className="pointer-events-none text-xs font-semibold fill-gray-800"
                fontSize="12"
              >
                {unitNumber.split('-').slice(0, -1).join('-')}
              </text>
            </g>
          ))}

        {/* Current Polygon Being Traced - Render after existing polygons for proper z-order */}
        {/* Only render if not hidden - always render if currentPolygon has points unless explicitly hidden */}
        {(() => {
          // Always render if we have points, unless the polygon is explicitly hidden
          const isHidden = currentPolygonKey !== null && currentPolygonKey !== undefined && hiddenPolygons.has(currentPolygonKey);
          const shouldRender = currentPolygon.length > 0 && !isHidden;
          
          console.log('MapCanvas: Current polygon rendering check (EVERY RENDER)', {
            currentPolygonLength: currentPolygon.length,
            currentPolygonKey,
            isHidden,
            shouldRender,
            hiddenPolygons: Array.from(hiddenPolygons),
            allPoints: currentPolygon.map((p, i) => ({ index: i, x: p[0], y: p[1] })),
            pointsPreview: currentPolygon.slice(0, 5).map(p => `[${p[0].toFixed(1)}, ${p[1].toFixed(1)}]`),
            viewBox: svgRef.current?.viewBox.baseVal ? {
              x: svgRef.current.viewBox.baseVal.x,
              y: svgRef.current.viewBox.baseVal.y,
              width: svgRef.current.viewBox.baseVal.width,
              height: svgRef.current.viewBox.baseVal.height,
            } : 'no SVG ref',
            zoomedViewBox: `${zoomedX} ${zoomedY} ${zoomedWidth} ${zoomedHeight}`,
            baseViewBox: `${vbX} ${vbY} ${vbWidth} ${vbHeight}`,
          });
          
          return shouldRender;
        })() && (
          <g style={{ pointerEvents: 'all' }}>
            {/* Lines between points - Render first, make clickable to insert points */}
            {(() => {
              const hasLines = currentPolygon.length > 1;
              console.log('MapCanvas: Rendering polyline', {
                hasLines,
                currentPolygonLength: currentPolygon.length,
                pointsString,
                pointsArray: currentPolygon,
              });
              return hasLines;
            })() && (
              <polyline
                points={pointsString}
                fill="none"
                stroke="#3b82f6"
                strokeWidth="8"
                strokeDasharray="5,5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="cursor-crosshair hover:stroke-blue-500"
                style={{ 
                  opacity: 0.8,
                  pointerEvents: 'all',
                  fill: 'none'
                }}
                onClick={handleLineSegmentClick}
              >
                <title>Click on line to insert a point</title>
              </polyline>
            )}

            {/* Polygon Preview - Make draggable with better visual feedback */}
            {(() => {
              const hasPolygon = currentPolygon.length >= 3;
              console.log('MapCanvas: Rendering filled polygon', {
                hasPolygon,
                currentPolygonLength: currentPolygon.length,
                pointsString,
                pointsArray: currentPolygon,
                draggingPolygon,
                fill: draggingPolygon ? "rgba(59, 130, 246, 0.5)" : "rgba(59, 130, 246, 0.3)",
                stroke: draggingPolygon ? "#1d4ed8" : "#3b82f6",
              });
              return hasPolygon;
            })() && (
              <g>
                <polygon
                  points={pointsString}
                  fill={draggingPolygon ? "rgba(59, 130, 246, 0.5)" : "rgba(59, 130, 246, 0.3)"}
                  stroke={draggingPolygon ? "#1d4ed8" : "#3b82f6"}
                  strokeWidth={draggingPolygon ? "3" : "2"}
                  strokeDasharray="5,5"
                  data-current-polygon="true"
                  className={onPolygonMove ? "cursor-move hover:fill-blue-400/40" : "pointer-events-none"}
                  style={{ pointerEvents: onPolygonMove ? 'all' : 'none' }}
                />
                {/* Center handle for moving polygon */}
                {onPolygonMove && currentPolygon.length >= 3 && (
                  <circle
                    cx={currentPolygon.reduce((sum, p) => sum + p[0], 0) / currentPolygon.length}
                    cy={currentPolygon.reduce((sum, p) => sum + p[1], 0) / currentPolygon.length}
                    r="4"
                    fill="#3b82f6"
                    stroke="white"
                    strokeWidth="1.5"
                    className="pointer-events-none"
                    opacity="0.8"
                  />
                )}
              </g>
            )}

            {/* Points - Render last so they appear on top */}
            {(() => {
              const hasPoints = currentPolygon.length > 0;
              console.log('MapCanvas: Rendering points container', {
                hasPoints,
                currentPolygonLength: currentPolygon.length,
                allPoints: currentPolygon.map((p, i) => ({ index: i, x: p[0], y: p[1], valid: !isNaN(p[0]) && !isNaN(p[1]) })),
              });
              return hasPoints;
            })() && (
              <g>
                {currentPolygon.map((point, index) => {
                  const [x, y] = point;
                  if (!point || point.length !== 2 || !x || !y || isNaN(x) || isNaN(y)) {
                    console.warn(`MapCanvas: Invalid point at index ${index}:`, { point, x, y, isNaNX: isNaN(x), isNaNY: isNaN(y) });
                    return null;
                  }
                  console.log(`MapCanvas: Rendering point ${index}:`, { 
                    x, 
                    y, 
                    point, 
                    viewBox: zoomedViewBox,
                    baseViewBox: `${vbX} ${vbY} ${vbWidth} ${vbHeight}`,
                    isInViewBox: x >= vbX && x <= vbX + vbWidth && y >= vbY && y <= vbY + vbHeight,
                    svgViewBox: svgRef.current?.viewBox.baseVal ? {
                      x: svgRef.current.viewBox.baseVal.x,
                      y: svgRef.current.viewBox.baseVal.y,
                      width: svgRef.current.viewBox.baseVal.width,
                      height: svgRef.current.viewBox.baseVal.height,
                    } : 'no SVG ref',
                  });
                  return (
                    <g key={`point-${index}`} style={{ pointerEvents: 'all' }}>
                      {/* Point circle - Make draggable, ensure visibility with larger size */}
                      <circle
                        cx={x}
                        cy={y}
                        r={hoveredPointIndex === index ? 12 : 10}
                        fill={hoveredPointIndex === index ? '#1d4ed8' : '#3b82f6'}
                        stroke="white"
                        strokeWidth="3"
                        data-point-index={index}
                        className={onPointMove ? "cursor-move" : "cursor-pointer"}
                        style={{ 
                          pointerEvents: 'all',
                          filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.7))',
                          opacity: 1,
                          zIndex: 1000
                        }}
                        onClick={(e) => handlePointClick(e, index)}
                        onMouseEnter={() => setHoveredPointIndex(index)}
                        onMouseLeave={() => setHoveredPointIndex(null)}
                      />
                  {/* Point number label */}
                  <text
                    x={x}
                    y={y - 14}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    className="pointer-events-none text-xs font-bold fill-white"
                    fontSize="11"
                    stroke="#1d4ed8"
                    strokeWidth="2"
                    style={{ 
                      filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.7))',
                      paintOrder: 'stroke fill',
                      opacity: 1
                    }}
                  >
                    {index + 1}
                  </text>
                  {/* Debug: Show coordinates - Always visible for debugging */}
                  <text
                    x={x}
                    y={y + 22}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    className="pointer-events-none text-[9px] fill-gray-800 font-mono"
                    fontSize="9"
                    style={{ 
                      filter: 'drop-shadow(0 1px 2px rgba(255,255,255,0.9))',
                      opacity: 0.9
                    }}
                  >
                    ({Math.round(x)}, {Math.round(y)})
                  </text>
                    </g>
                  );
                })}
              </g>
            )}
          </g>
        )}

        {/* Debug: Show last click location */}
        {lastClickPoint && (
          <g>
            <circle
              cx={lastClickPoint[0]}
              cy={lastClickPoint[1]}
              r="4"
              fill="red"
              stroke="white"
              strokeWidth="1"
              opacity="0.8"
              style={{ pointerEvents: 'none' }}
            />
            <text
              x={lastClickPoint[0]}
              y={lastClickPoint[1] - 8}
              textAnchor="middle"
              className="pointer-events-none text-[8px] fill-red-600 font-bold"
              fontSize="8"
            >
              CLICK
            </text>
          </g>
        )}
      </svg>
    </div>
  );
}

